// message.ts

// 定义消息类型（字面量联合类型）
type MessageType = "success" | "error" | "info" | "warning";

// 定义返回的 message 对象结构（可选，但便于复用）
interface Message {
  success(text: string, duration?: number): void;
  error(text: string, duration?: number): void;
  info(text: string, duration?: number): void;
  warning(text: string, duration?: number): void;
}

// 由于 widget 使用 Shadow DOM，外部 message-container 拿不到 Shadow DOM 内的 less 样式，
// 所以这里把样式直接以 inline style 写在元素上（与 message-mask 在 .vue 中内联样式同思路）。
// 视觉风格对齐 ChatDialog.vue 的 .message-mask：浅色底 + 同色调文字 + box-shadow，无边框。
const TYPE_COLORS: Record<MessageType, { background: string; color: string }> =
  {
    success: { background: "#F0FAF4", color: "#2e9b4a" },
    error: { background: "#FFF5F6", color: "#d93025" },
    info: { background: "#F0F7FF", color: "#1677FF" },
    warning: { background: "#FFF7E6", color: "#E88E00" },
  };

const KEYFRAMES_STYLE_ID = "abot-widget-message-keyframes";

// 注入仅用于 message 的动画 keyframes（一次性，全局）
function ensureKeyframes(): void {
  if (document.getElementById(KEYFRAMES_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_STYLE_ID;
  style.textContent = `
@keyframes abotWidgetMessageSlideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes abotWidgetMessageFadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
`;
  document.head.appendChild(style);
}

// 使用模块作用域，避免污染全局
let container: HTMLElement | null = null;

// 获取或创建消息容器（带 inline 样式）
function getContainer(): HTMLElement {
  if (!container || !container.isConnected) {
    container = document.querySelector(
      ".abot-widget-message-container",
    ) as HTMLElement | null;
    if (!container) {
      container = document.createElement("div");
      container.className = "abot-widget-message-container";
      Object.assign(container.style, {
        position: "fixed",
        top: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: "9999",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pointerEvents: "none",
      } as Partial<CSSStyleDeclaration>);
      document.body.appendChild(container);
    }
  }
  return container;
}

// 核心显示函数
function showMessage(
  text: string,
  type: MessageType = "info",
  duration: number = 3000,
): void {
  ensureKeyframes();
  const container = getContainer();

  const { background, color } = TYPE_COLORS[type];

  const item = document.createElement("div");
  item.textContent = text;
  Object.assign(item.style, {
    background,
    color,
    boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.10)",
    borderRadius: "2px",
    padding: "9px 16px",
    marginBottom: "10px",
    fontSize: "14px",
    lineHeight: "1.5",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    pointerEvents: "auto",
    animation: "abotWidgetMessageSlideDown 0.3s ease",
  } as Partial<CSSStyleDeclaration>);

  container.appendChild(item);

  // 定时移除
  setTimeout(() => {
    item.style.animation = "abotWidgetMessageFadeOut 0.3s ease forwards";
    setTimeout(() => {
      if (item.parentNode) {
        item.remove();
      }
    }, 300);
  }, duration);
}

// 导出 message 对象
export const message: Message = {
  success: (text, duration) => showMessage(text, "success", duration),
  error: (text, duration) => showMessage(text, "error", duration),
  info: (text, duration) => showMessage(text, "info", duration),
  warning: (text, duration) => showMessage(text, "warning", duration),
};
